import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import PropTypes from 'prop-types';
import TableHeader from 'components/Table/TableHeader';

const initFind = {
  search: '',
  order_by: 'r.date_record',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

const initSummary = {
  quantity: '',
  amount: '',
  discount: '',
  total_discount: '',
  net: '',
};

function QueryPurchaseHistory({ productCode, setLoading }) {
  const mod = 'rp_product_query';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [summary, setSummary] = useState(initSummary);
  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);

  const initHeader = [
    { id: 'date_record', label: 'วันที่เอกสาร', align: 'left' },
    { id: 'receive_code', label: 'เลขที่เอกสาร', align: 'left' },
    { id: 'supplier_code', label: 'รหัสเจ้าหนี้', align: 'center' },
    { id: 'unit_name', label: 'หน่วยนับ', align: 'center' },
    { id: 'quantity_receive', label: 'ซื้อ', align: 'right' },
    { id: 'quantity_free', label: 'แถม', align: 'right' },
    { id: 'price', label: 'ราคา', align: 'right' },
    { id: 'discount_unit', label: 'ส่วนลด', align: 'right' },
    { id: 'total', label: 'มูลค่า', align: 'right' },
    { id: 'discount', label: 'ส่วนลดรวม', align: 'right' },
    { id: 'amount', label: 'สุทธิ', align: 'right' },
    { id: 'per_unit', label: 'ต่อหน่วย', align: 'right' },
    { id: 'barcode2', label: 'รหัสซื้อขาย', align: 'right' },
    { id: 'supplier_name', label: 'เจ้าหนี้', align: 'right' },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('OPTIONS', `/${mod}/purchase_history`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setPageTotal(result.total_pages);
        setPageNo(result.page);
        setSummary(result.summary);
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    if (productCode) view({ ...find, search: productCode, page: 1 });
  }, [productCode]);

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const renderList = () => {
    return (
      <>
        <TableContainer component={Paper} sx={{ minHeight: 400 }}>
          <Table aria-label='datatable table'>
            <TableHeader
              order={order}
              orderBy={orderBy}
              onRequestSort={handleSort}
              data={initHeader}
            />
            <TableBody>
              {record.length === 0 || !productCode ? (
                <TableRow>
                  <TableCell colSpan={initHeader.length} align='center'>
                    ไม่พบข้อมูล
                  </TableCell>
                </TableRow>
              ) : (
                record.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell align='center'>{Func.dateDisplay(row.date_record)}</TableCell>
                    <TableCell align='center' className='nowrap'>
                      {row.receive_code}
                    </TableCell>
                    <TableCell align='center' className='nowrap'>
                      {row.supplier_code}
                    </TableCell>
                    <TableCell align='center'>{row.unit_name}</TableCell>
                    <TableCell align='right' className='nowrap'>
                      {Func.numberDisplay(row.quantity_receive)}
                    </TableCell>
                    <TableCell align='right' className='nowrap'>
                      {Func.numberDisplay(row.quantity_free)}
                    </TableCell>
                    <TableCell align='right' className='nowrap'>
                      {Func.numberDisplay(row.price, 2)}
                    </TableCell>
                    <TableCell align='right' className='nowrap'>
                      {row.discount_unit > 0 ? Func.numberDisplay(row.discount_unit, 2) : '-'}
                    </TableCell>
                    <TableCell align='right' className='nowrap'>
                      {Func.numberDisplay(row.total, 2)}
                    </TableCell>
                    <TableCell align='right' className='nowrap'>
                      {row.discount > 0 ? Func.numberDisplay(row.discount, 2) : '-'}
                    </TableCell>
                    <TableCell align='right' className='nowrap'>
                      {Func.numberDisplay(row.amount, 2)}
                    </TableCell>
                    <TableCell align='right' className='nowrap'>
                      {Func.numberDisplay(row.per_unit, 2)}
                    </TableCell>
                    <TableCell align='center' className='nowrap'>
                      {row.barcode}
                    </TableCell>
                    <TableCell className='nowrap'>{row.supplier_name}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Grid item xs={12} sx={{ mt: 3 }}>
          <Card sx={{ p: 2 }}>
            <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
              <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                {t(`element.show_record`, { num: find.perPage })}&nbsp;
                <Icon>keyboard_arrow_down</Icon>
              </MDButton>
              {renderShow}
              <Pagination
                count={pageTotal}
                variant='outlined'
                color='secondary'
                showFirstButton
                showLastButton
                page={pageNo}
                onChange={(e, p) => view({ ...find, page: p })}
              />
            </MDBox>
          </Card>
        </Grid>
      </>
    );
  };

  return <>{renderList()}</>;
}

QueryPurchaseHistory.propTypes = {
  productCode: PropTypes.string.isRequired,
  setLoading: PropTypes.func.isRequired,
};

export default QueryPurchaseHistory;
